import React, { useState } from 'react';
import { getAppName, isDevelopmentMode } from '../utils/config';
import { formatVersion, getCurrentVersionInfo } from '../utils/version';
import FullScreenLayout from './FullScreenLayout';

interface VersionIndicatorProps {
  className?: string;
  showBuildNumber?: boolean;
  showEnvironment?: boolean;
  onClick?: () => void;
}

export const VersionIndicator: React.FC<VersionIndicatorProps> = ({
  className = '',
  showBuildNumber = true,
  showEnvironment = true,
  onClick
}) => {
  const [showFullScreen, setShowFullScreen] = useState(false);
  
  // Only show in development mode
  if (!isDevelopmentMode()) {
    return null;
  }

  const versionInfo = getCurrentVersionInfo();
  const appName = getAppName();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default behavior: show full screen version info
      setShowFullScreen(true);
    }
  };

  const handleBack = () => {
    setShowFullScreen(false);
  };

  if (showFullScreen) {
    return (
      <div className="fixed inset-0 bg-white z-[9999]">
        <FullScreenLayout
          title="Version Information"
          showBackButton={true}
          onBack={handleBack}
          backButtonText="Close"
        >
          <div className="space-y-6">
            {/* App Info */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{appName}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Version:</span>
                  <span className="ml-2 text-gray-900">{formatVersion(versionInfo.version)}</span>
                </div>
                {showBuildNumber && (
                  <div>
                    <span className="font-semibold text-gray-700">Build:</span>
                    <span className="ml-2 text-gray-900">{versionInfo.buildNumber}</span>
                  </div>
                )}
                {showEnvironment && (
                  <div>
                    <span className="font-semibold text-gray-700">Environment:</span>
                    <span className="ml-2 text-gray-900 uppercase">{versionInfo.environment}</span>
                  </div>
                )}
                <div>
                  <span className="font-semibold text-gray-700">Build Date:</span>
                  <span className="ml-2 text-gray-900">{new Date(versionInfo.buildDate).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Version Details */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Version Details</h3>
              <div className="text-sm text-gray-700">
                <p>This is the version information for the {appName} application.</p>
                <p className="mt-2">Current version: <span className="font-mono font-semibold">{formatVersion(versionInfo.version)}</span></p>
              </div>
            </div>

            {/* Development Info */}
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Development Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold text-gray-700">Node Environment:</span>
                  <span className="ml-2 text-gray-900">{process.env.NODE_ENV}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">React Version:</span>
                  <span className="ml-2 text-gray-900">{React.version}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Build Time:</span>
                  <span className="ml-2 text-gray-900">{new Date().toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </FullScreenLayout>
      </div>
    );
  }

  return (
    <div
      className={`
        fixed top-4 right-4 md:top-auto md:bottom-4 bg-red-500 text-white text-md px-2 py-1 rounded opacity-90 hover:opacity-100 transition-opacity z-[9999] cursor-pointer
        ${className}
      `}
      onClick={handleClick}
      title={`${appName} - ${formatVersion(versionInfo.version)}`}
    >
        <div className="flex items-center gap-2">Experts</div>
      <div className="font-bold">{formatVersion(versionInfo.version)}</div>
      {/* {showBuildNumber && (
        <div className="text-xs opacity-75">Build {versionInfo.buildNumber}</div>
      )}
      {showEnvironment && (
        <div className="text-xs opacity-75 uppercase">{versionInfo.environment}</div>
      )} */}
    </div>
  );
};

export default VersionIndicator; 